import { useState } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { AcceptedCfrFields, CreateTemplateValidation, useCreateCfrTemplate } from "@/lib/api/cfr";
import { useListMarketZones } from "@/lib/api/generalContract";
import { CommodityOptions } from "@/lib/api/commodity";
import { safeParseDecimal } from "@/lib/api/common";
import { PdfField } from "../types";
import { SystemFieldType } from "@/lib/api/signingContracts";
import { UploadPresignedFile } from "@/lib/file";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useListCustomers } from "@/lib/api/customer";

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: PdfField[];
  originalFile: File;
  onSuccess: () => void;
}

interface TemplateFormData {
  commodity: string;
  crop_year: number;
  destination_id?: string;
  customer_destination_id?: string;
  version_label: string;
  version_reason: string;
  name: string;
}

function generateDefaultFormData({ fields, hasFixedCommodity }: { fields: PdfField[]; hasFixedCommodity: boolean }): TemplateFormData {
  const base_commodity = fields.find(field => field.field_id === AcceptedCfrFields.CONTRACT_COMMODITY)?.freeform_value || "C";
  const commodity_label = CommodityOptions.find(item => item.value === base_commodity)?.label || base_commodity;
  const crop_year = parseInt(moment.tz(CommonDestinationTimezone).format("YYYY"));
  return {
    commodity: base_commodity,
    crop_year,
    destination_id: undefined,
    customer_destination_id: undefined,
    version_label: "v1",
    version_reason: "v1",
    name: [crop_year?.toString(), commodity_label, "CFR Declaration"].filter(Boolean).join(" "),
  }
}

export function CreateTemplateDialog({ open, onOpenChange, fields, originalFile, onSuccess }: CreateTemplateDialogProps) {
  const hasFixedCommodity = !!fields.find(field => field.field_id === AcceptedCfrFields.CONTRACT_COMMODITY)?.freeform_value;

  const { toast } = useToast();
  const createCfrTemplates = useCreateCfrTemplate();
  const customersResult = useListCustomers({ minimal: true});
  const [formData, setFormData] = useState<TemplateFormData>(generateDefaultFormData({ fields, hasFixedCommodity }));
  const [isSubmitting, setIsSubmitting] = useState(false);


  const customers = customersResult?.data?.data || [];
  const customerOptions = customers.map((customer: { id: string; long_name: string }) => ({
    value: customer.id,
    label: customer.long_name
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.commodity || !formData.version_label) {
      toast({
        title: "Error",
        description: "Commodity and version label are required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const processedFields = fields.map(field => ({
        field_type: field.field_type,
        field_id: field.field_id,
        field_key: field.field_key,
        system_field_type: field.system_field_type,
        field_dest_type: field.field_dest_type || genFieldDestType(field.field_type, field.field_id),
        position_data: {
          x: safeParseDecimal({})(field.position_data.x).toString(),
          y: safeParseDecimal({})(field.position_data.y).toString(),
          width: safeParseDecimal({})(field.position_data.width).toString(),
          height: safeParseDecimal({})(field.position_data.height).toString(),
          text_size: safeParseDecimal({})(field.position_data.text_size).toNumber(),
          page: safeParseDecimal({})(field.position_data.page).toNumber(),
        },
        freeform_value: field.freeform_value,
        signer_id: "1",
        is_required: field.is_required ?? false,
        is_readonly: field.is_readonly ?? false,
      }));

      const parseResult = CreateTemplateValidation.safeParse({
        commodity: formData.commodity,
        crop_year: formData.crop_year,
        destination_id: formData.destination_id,
        customer_destination_id: formData.customer_destination_id,
        template_content_type: "cfr",
        parent_group_id: uuidV4(),
        template: {
          name: formData.name ? formData.name : undefined,
          version_label: formData.version_label,
          version_reason: formData.version_reason,
          require_all_signers: true,
          send_auto_reminders: false,
          use_signer_order: true,
          fields: processedFields,
        }
      });

      if (!parseResult.success) {
        console.error("failed to parse api body", parseResult.error);
        toast({
          title: "Error",
          description: "Invalid data",
          variant: "destructive",
        });
        return;
      }

      const apiResult = await createCfrTemplates(parseResult.data)
      if (!apiResult.success) {
        throw apiResult;
      }

      const uploadResult = await UploadPresignedFile(apiResult.data.upload_url, originalFile)
      if (!uploadResult.success) {
        toast({
          title: "Error",
          description: "Failed to upload file",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Created Template",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  console.warn("template", formData.commodity)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create CFR Template</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="commodity">Commodity</Label>
              <Combobox
                disabled={hasFixedCommodity}
                className="w-full"
                popoverClassName="min-w-[400px]"
                options={CommodityOptions}
                value={formData.commodity}
                onValueChange={(value) => setFormData(prev => ({ ...prev, commodity: value }))}
                placeholder="Select commodity..."
                dwimOnChange
                isModal
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="crop_year">Crop Year</Label>
              <Input
                id="crop_year"
                type="number"
                value={formData.crop_year}
                onChange={(e) => setFormData(prev => ({ ...prev, crop_year: parseInt(e.target.value) || 2025 }))}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer_destination_id">Destination (Optional)</Label>
              <Combobox
                className="w-full"
                popoverClassName="min-w-[450px]"
                options={customerOptions}
                value={formData.customer_destination_id || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, customer_destination_id: value || undefined }))}
                placeholder="Select destination..."
                virtualizeList
                dwimOnChange
                isModal
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="v1"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="version_label">Version Label</Label>
              <Input
                id="version_label"
                value={formData.version_label}
                onChange={(e) => setFormData(prev => ({ ...prev, version_label: e.target.value }))}
                placeholder="v1"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="version_reason">Version Reason (Optional)</Label>
              <Textarea
                id="version_reason"
                value={formData.version_reason}
                onChange={(e) => setFormData(prev => ({ ...prev, version_reason: e.target.value }))}
                placeholder="Enter reason for this version..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function genFieldDestType(field_type: string, field_id: string) {
  if (field_type !== 'signature') {
    return undefined;
  }

  if (/date/i.test(field_id || '')) {
    return 'date';
  }

  return 'signature';
}

